import * as types from './types';
import { isServiceError } from '../../utils/errorCodes';

/**
 * Verifica si hay cambios entre el estado actual y el original
 * @param {Object} currentFeatures - Estado actual de características
 * @param {Object} originalFeatures - Estado original de características
 * @returns {boolean} True si hay cambios, False si no
 */
const checkForChangesInState = (currentFeatures, originalFeatures) => {
  // Comparar feature checks
  const checksChanged = currentFeatures.featureChecks.some(current => {
    const original = originalFeatures.featureChecks.find(orig => orig.id === current.id);
    return original?.isChecked !== current.isChecked;
  });

  // Comparar feature selects
  const selectsChanged = currentFeatures.featureSelect.some(current => {
    const original = originalFeatures.featureSelect.find(orig => orig.id === current.id);
    return original?.value !== current.value;
  });

  // Comparar feature inputs
  const inputsChanged = currentFeatures.featureInputs.some(current => {
    const original = originalFeatures.featureInputs.find(orig => orig.id === current.id);
    const currentValue = current.value === undefined ? '' : String(current.value);
    const originalValue = original?.value === undefined ? '' : String(original?.value || '');
    return original && currentValue !== originalValue;
  });

  return checksChanged || selectsChanged || inputsChanged;
};

/**
 * Procesa las características recibidas del servicio
 * @param {Object} features - Características recibidas
 * @returns {Object} Datos procesados
 */
const processFeatures = features => {
  const featureChecks = [];
  const featureSelect = [];
  const featureInputs = [];
  const processedCategories = {};

  // Procesamos todas las categorías excepto la category-14
  Object.entries(features || {}).forEach(([categoryId, category]) => {
    if (categoryId === 'category-14') return;

    // Guardamos la categoría procesada para usar directamente en los componentes
    processedCategories[categoryId] = {
      ...category,
      // Aseguramos que todas las opciones estén combinadas y filtradas
      processedOptions: [...(category.mostUsedFeatures || []), ...(category.features || [])].filter(Boolean)
    };

    // Agregamos a featureChecks
    processedCategories[categoryId].processedOptions.forEach(feature => {
      if (feature?.id) {
        featureChecks.push({
          id: feature.id,
          isChecked: Boolean(feature.isChecked),
          touched: false
        });
      }
    });
  });

  // Procesamos la categoría category-14 (categorías adicionales)
  const additionalCategories = features?.['category-14'];
  if (additionalCategories) {
    // Guardamos las opciones procesadas para usar directamente en el componente
    processedCategories['category-14'] = {
      ...additionalCategories,
      processedOptions: [
        ...(additionalCategories.mostUsedFeatures || []),
        ...(additionalCategories.features || [])
      ].filter(Boolean)
    };

    processedCategories['category-14'].processedOptions.forEach(feature => {
      if (!feature || !feature.id) return;

      if (feature.fieldType === 'Select') {
        featureSelect.push({
          id: feature.id,
          value: feature.value || '',
          touched: false
        });
      } else {
        featureInputs.push({
          id: feature.id,
          value: feature.value || '',
          touched: false
        });
      }
    });
  }

  // Se hacen copias del estado original
  const originalFeatureChecks = featureChecks.map(item => ({ ...item }));
  const originalFeatureSelect = featureSelect.map(item => ({ ...item }));
  const originalFeatureInputs = featureInputs.map(item => ({ ...item }));

  return {
    processedCategories,
    featureChecks,
    featureSelect,
    featureInputs,
    originalFeatureChecks,
    originalFeatureSelect,
    originalFeatureInputs
  };
};

/**
 * Actualiza un checkbox y verifica cambios
 * @param {Object} state - Estado actual
 * @param {Object} payload - Datos de la acción
 * @returns {Object} Nuevo estado
 */
const handleUpdateFeatureCheckbox = (state, payload) => {
  const { id, isChecked } = payload;
  // Verificar que el ID existe en el estado
  const featureExists = state.featureChecks.some(f => f.id === id);
  if (!featureExists) return state;

  const updatedFeatureChecks = state.featureChecks.map(feature =>
    feature.id === id ? { ...feature, isChecked, touched: true } : feature
  );

  // Verificamos si hay cambios
  const hasChanges = checkForChangesInState(
    {
      featureChecks: updatedFeatureChecks,
      featureSelect: state.featureSelect,
      featureInputs: state.featureInputs
    },
    {
      featureChecks: state.originalFeatureChecks,
      featureSelect: state.originalFeatureSelect,
      featureInputs: state.originalFeatureInputs
    }
  );

  return {
    ...state,
    featureChecks: updatedFeatureChecks,
    hasChanges
  };
};

/**
 * Actualiza un select y verifica cambios
 * @param {Object} state - Estado actual
 * @param {Object} payload - Datos de la acción
 * @returns {Object} Nuevo estado
 */
const handleUpdateFeatureSelect = (state, payload) => {
  const { id, value } = payload;
  // Verificar que el ID existe en el estado
  const featureExists = state.featureSelect.some(f => f.id === id);
  if (!featureExists) return state;

  const updatedFeatureSelect = state.featureSelect.map(feature =>
    feature.id === id ? { ...feature, value, touched: true } : feature
  );

  // Verificamos si hay cambios
  const hasChanges = checkForChangesInState(
    {
      featureChecks: state.featureChecks,
      featureSelect: updatedFeatureSelect,
      featureInputs: state.featureInputs
    },
    {
      featureChecks: state.originalFeatureChecks,
      featureSelect: state.originalFeatureSelect,
      featureInputs: state.originalFeatureInputs
    }
  );

  return {
    ...state,
    featureSelect: updatedFeatureSelect,
    hasChanges
  };
};

/**
 * Actualiza un input y verifica cambios
 * @param {Object} state - Estado actual
 * @param {Object} payload - Datos de la acción
 * @returns {Object} Nuevo estado
 */
const handleUpdateFeatureInput = (state, payload) => {
  const { id, value } = payload;
  // Verificar que el ID existe en el estado
  const featureExists = state.featureInputs.some(f => f.id === id);
  if (!featureExists) return state;

  const updatedFeatureInputs = state.featureInputs.map(feature =>
    feature.id === id ? { ...feature, value: value || '', touched: true } : feature
  );

  // Verificamos si hay cambios
  const hasChanges = checkForChangesInState(
    {
      featureChecks: state.featureChecks,
      featureSelect: state.featureSelect,
      featureInputs: updatedFeatureInputs
    },
    {
      featureChecks: state.originalFeatureChecks,
      featureSelect: state.originalFeatureSelect,
      featureInputs: state.originalFeatureInputs
    }
  );

  return {
    ...state,
    featureInputs: updatedFeatureInputs,
    hasChanges
  };
};

const initialState = {
  loading: false,
  error: false,
  features: {},
  // Estados para características procesadas
  featureChecks: [],
  featureSelect: [],
  featureInputs: [],
  // Estado original para comparaciones
  originalFeatureChecks: [],
  originalFeatureSelect: [],
  originalFeatureInputs: [],
  // Estado para tracking de cambios
  hasChanges: false
};

function extraFeaturesReducer(state = initialState, action) {
  switch (action.type) {
    case types.GET_FEATURES:
      return { ...state, loading: true, error: false };

    case types.GET_FEATURES_COMPLETED: {
      const processedData = processFeatures(action.payload.features);

      return {
        ...state,
        ...processedData,
        loading: false,
        hasChanges: false,
        minSuggestedFeatures: action.payload.minSuggestedFeatures || '2', // 2 por default para no afectar la traducción en plural
        isEmpty: Object.keys(action?.payload?.features).length === 0
      };
    }

    case types.GET_FEATURES_FAILED:
      return {
        ...state,
        loading: false,
        error: isServiceError(action?.payload?.status)
      };

    case types.UPDATE_FEATURE_CHECKBOX:
      return handleUpdateFeatureCheckbox(state, action.payload);

    case types.UPDATE_FEATURE_SELECT:
      return handleUpdateFeatureSelect(state, action.payload);

    case types.UPDATE_FEATURE_INPUT:
      return handleUpdateFeatureInput(state, action.payload);

    case types.RESET_STATE:
      return {
        ...initialState
      };

    default:
      return state;
  }
}

export { extraFeaturesReducer };
